"use client";
import axios from "axios";
import Castle from "@castleio/castle-js";
import { PublicConfig } from "@/shared/PublicConfig";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { UnderwriterConfigKey } from "@/shared/types/Quote.interface";
import { CookieUtils } from "@/shared/utils/CookieUtils";
import { BuilderUtils } from "@/shared/utils/BuilderUtils";
import { QuoteDataUtils } from "@/shared/utils/QuoteDataUtils";
import { PetQuote } from "@spotpetinsurance/spot-types/entities/PetQuote";

// Extract the ConfigureResponse type
type CastleInstance = ReturnType<typeof Castle.configure>;

// Only initialize Castle on the client side
let castle: CastleInstance | null = null;
let clientIp: string | undefined = undefined;
const basePath = PublicConfig.BASE_PATH;

// Initialize Castle only in browser environment
if (typeof window !== "undefined") {
    castle = Castle.configure({ pk: PublicConfig.CASTLE_IO_PUBLIC_API_KEY });
}

async function getClientIp(): Promise<string | undefined> {
    // Only run on client side
    if (typeof window === "undefined") return undefined;

    if (!clientIp) {
        try {
            const ipResponse = await axios.get(`${basePath}/api/info`);
            clientIp = ipResponse.data.ipAddress;
        } catch (error) {
            console.error("Error fetching client IP:", error);
        }
    }
    return clientIp;
}

async function createCastleToken(): Promise<string | undefined> {
    // Only run on client side and if castle is available
    if (typeof window === "undefined" || !castle) return undefined;

    try {
        return await castle.createRequestToken();
    } catch (error) {
        console.error("Error creating Castle request token:", error);
        return undefined;
    }
}

const accountRoutesToIncludeCastleToken = ["stripe/payment-method/setup", "stripe/payment-method/finalize"];

// Only set up interceptor on client side
if (typeof window !== "undefined") {
    axios.interceptors.request.use(
        async function (config) {
            const isQuoteApiRequest = !!config.url?.includes("quoting/quotes");
            const isAddPetQuoteApiRequest = !!config.url?.includes("/addapet/");
            const isScratchpadApiRequest = !!config.url?.includes("scratchpad");
            // The paperless endpoint ignores `extra` (it only reads paperless/mailing), so skip the
            // Castle/analytics enrichment for it — no point minting a fraud token + IP for a payload the
            // server strips.
            const isPaperlessApiRequest = !!config.url?.includes("paperless");
            const hasDataPayload = !!config.data && typeof config.data === "object";
            const isAccountApiRequest = !!config.url && accountRoutesToIncludeCastleToken.some(route => config.url?.includes(route));

            if (isAccountApiRequest) {
                const castleToken = await createCastleToken();
                config.data = {
                    ...config.data,
                    castleToken: castleToken
                };
            }

            if (isQuoteApiRequest && !isScratchpadApiRequest && !isPaperlessApiRequest && hasDataPayload) {
                const data = config.data as PetQuote;
                const [token, ip] = await Promise.all([createCastleToken(), getClientIp()]);

                const existingParamsInQuote = data?.extra?.queryParams;
                const existingEmailInQuote = data?.email;
                const existingTestUserIdInQuote = data?.extra?.testUserId;

                const browserExtraData = AnalyticsUtils.constructExtra(existingParamsInQuote, existingEmailInQuote, existingTestUserIdInQuote);

                config.data.extra = {
                    ...config.data.extra,
                    ipAddress: ip,
                    castleToken: token,
                    ...browserExtraData
                };

                const underwriter = config.data?.underwriter;
                const quoteId = config.data?.quoteId;
                if (!!underwriter && !!quoteId && typeof underwriter === "string" && typeof quoteId === "string") {
                    // Quote ID cookie
                    const uwConfigKey = underwriter.replace("-", "_").toUpperCase() as UnderwriterConfigKey;
                    const cookieId = PublicConfig?.[uwConfigKey]?.COOKIE_QUOTE_ID_KEY;
                    const existingCookie = CookieUtils.get(cookieId);

                    if (existingCookie !== quoteId) {
                        CookieUtils.set(cookieId, quoteId, { expires: 365 });
                    }

                    // Quote Attributes cookie
                    const cookieAttributesKey = PublicConfig?.[uwConfigKey]?.COOKIE_QUOTE_ATTRIBUTES_KEY;

                    if (!!cookieAttributesKey) {
                        const quoteAttributes = BuilderUtils.getBuilderAttributes({ data: QuoteDataUtils.petQuoteToQuote(data) });
                        const existingAttrs = CookieUtils.getJSON<Record<string, unknown>>(cookieAttributesKey);
                        const giftCardProvider = existingAttrs?.giftCardProvider;
                        const merged = giftCardProvider !== undefined ? { ...quoteAttributes, giftCardProvider } : quoteAttributes;
                        CookieUtils.set(cookieAttributesKey, JSON.stringify(merged), { expires: 365 });
                    }
                }
            }
            // add-a-pet endpoint (/addapet/ptz-us/quotes/...) bypasses isQuoteApiRequest enrichment (Builder, Castle) above
            // (incl. UTMs forwarded from member center) to extra.queryParams so they survive to every add-a-pet call (quote update + finalize), mirroring the standard-quote behavior.
            if (isAddPetQuoteApiRequest && hasDataPayload) {
                const data = config.data as { extra?: { queryParams?: Record<string, string> } };
                config.data.extra = {
                    ...config.data.extra,
                    queryParams: AnalyticsUtils.getQueryParams(data?.extra?.queryParams)
                };
            }

            return config;
        },
        function (error) {
            // Handle request error
            return Promise.reject(error);
        }
    );
}
